import type {
  SynthesesGroupedContractNode,
  SynthesesGroupedContractsResponse,
  SynthesesGroupedFinanceurItem,
} from "./useSynthesesGroupedContractsRead";

type ContractMatcher = {
  contractNodeId?: string | null;
  synthesesContractId?: number | null;
  realFundingIds?: number[];
};

type FinanceurIdentity = {
  id: number | null;
  name: string;
  email?: string | null;
  isVerdeo?: boolean;
};

function toText(value: unknown) {
  return String(value ?? "").trim();
}

function toAmount(value: unknown) {
  const parsed = Number(value || 0);
  return Number((Number.isFinite(parsed) ? parsed : 0).toFixed(2));
}

function sameFinanceur(
  financeur: SynthesesGroupedFinanceurItem,
  identity: FinanceurIdentity,
) {
  const financeurId = Number(financeur.id || 0);
  const identityId = Number(identity.id || 0);
  if (financeurId > 0 && identityId > 0) {
    return financeurId === identityId;
  }

  return toText(financeur.name).toLocaleLowerCase() ===
    toText(identity.name).toLocaleLowerCase();
}

function contractMatches(
  contract: SynthesesGroupedContractNode,
  matcher: ContractMatcher,
) {
  const matcherContractId = Number(matcher.synthesesContractId || 0);
  if (matcherContractId > 0) {
    const contractId = Number(contract.synthesesContractId || 0);
    if (contractId > 0 && contractId === matcherContractId) {
      return true;
    }
  }

  const matcherNodeId = toText(matcher.contractNodeId);
  if (matcherNodeId && toText(contract.id) === matcherNodeId) {
    return true;
  }

  const matcherRealFundingIds = Array.isArray(matcher.realFundingIds)
    ? matcher.realFundingIds.filter((id) => Number(id) > 0)
    : [];
  if (matcherRealFundingIds.length === 0) return false;

  const contractRealFundingIds = new Set(
    [
      contract.realFundingId,
      ...(Array.isArray(contract.realFundingIds) ? contract.realFundingIds : []),
      ...(Array.isArray(contract.lineRows)
        ? contract.lineRows.map((line) => line.realFundingId)
        : []),
    ]
      .map((id) => Number(id || 0))
      .filter((id) => id > 0),
  );

  return matcherRealFundingIds.some((id) => contractRealFundingIds.has(id));
}

function recomputeContractTotals(
  contract: SynthesesGroupedContractNode,
): SynthesesGroupedContractNode {
  const allocationsEuro = toAmount(
    (Array.isArray(contract.allocations) ? contract.allocations : []).reduce(
      (sum, allocation) => sum + Number(allocation.amountEuro || 0),
      0,
    ),
  );
  const expectedPaymentsEuro = toAmount(
    (Array.isArray(contract.expectedPayments) ? contract.expectedPayments : [])
      .reduce((sum, payment) => sum + Number(payment.amountEuro || 0), 0),
  );
  const receivedPaymentsEuro = toAmount(
    (Array.isArray(contract.paidPayments) && contract.paidPayments.length > 0
      ? contract.paidPayments
      : (Array.isArray(contract.expectedPayments) ? contract.expectedPayments : [])
          .filter((payment) => Number(payment.status || 0) === 2)
    ).reduce((sum, payment) => sum + Number(payment.amountEuro || 0), 0),
  );
  const landPaymentsEuro = toAmount(
    (Array.isArray(contract.landPayments) ? contract.landPayments : [])
      .filter((payment) => Number(payment.status || 0) === 2)
      .reduce((sum, payment) => sum + Number(payment.amountEuro || 0), 0),
  );

  return {
    ...contract,
    totals: {
      allocationsEuro,
      expectedPaymentsEuro,
      receivedPaymentsEuro,
      landPaymentsEuro,
    },
  };
}

function recomputeFinanceurTotals(
  financeur: SynthesesGroupedFinanceurItem,
): SynthesesGroupedFinanceurItem {
  const contracts = (Array.isArray(financeur.contracts)
    ? financeur.contracts
    : []
  ).map(recomputeContractTotals);

  return {
    ...financeur,
    contracts,
    totals: {
      contractAmountEuro: toAmount(
        contracts.reduce((sum, contract) => sum + Number(contract.amountEuro || 0), 0),
      ),
      allocationsEuro: toAmount(
        contracts.reduce(
          (sum, contract) => sum + Number(contract.totals.allocationsEuro || 0),
          0,
        ),
      ),
      expectedPaymentsEuro: toAmount(
        contracts.reduce(
          (sum, contract) => sum + Number(contract.totals.expectedPaymentsEuro || 0),
          0,
        ),
      ),
      receivedPaymentsEuro: toAmount(
        contracts.reduce(
          (sum, contract) => sum + Number(contract.totals.receivedPaymentsEuro || 0),
          0,
        ),
      ),
      landPaymentsEuro: toAmount(
        contracts.reduce(
          (sum, contract) => sum + Number(contract.totals.landPaymentsEuro || 0),
          0,
        ),
      ),
    },
  };
}

function withFreshMetadata(
  data: SynthesesGroupedContractsResponse,
  items: SynthesesGroupedFinanceurItem[],
): SynthesesGroupedContractsResponse {
  return {
    ...data,
    generatedAt: new Date().toISOString(),
    source: data.source || "browser-cache",
    items: items.map(recomputeFinanceurTotals),
  };
}

function createEmptyResponse(): SynthesesGroupedContractsResponse {
  return {
    generatedAt: new Date().toISOString(),
    source: "browser-cache",
    view: "financeurs",
    counts: {
      realFundings: 0,
      financeurs: 0,
      projects: 0,
      intervenants: 0,
    },
    items: [],
  };
}

function createFinanceur(identity: FinanceurIdentity): SynthesesGroupedFinanceurItem {
  return {
    id: identity.id,
    name: identity.name || "Financeur",
    email: identity.email || null,
    isVerdeo: identity.isVerdeo === true,
    contracts: [],
    totals: {
      contractAmountEuro: 0,
      allocationsEuro: 0,
      expectedPaymentsEuro: 0,
      receivedPaymentsEuro: 0,
      landPaymentsEuro: 0,
    },
  };
}

function getSynthesesContractId(contract: SynthesesGroupedContractNode) {
  const contractId = Number(contract.synthesesContractId || 0);
  return Number.isFinite(contractId) && contractId > 0 ? contractId : null;
}

function uniqueNumbers(values: Array<number | null | undefined>) {
  return Array.from(
    new Set(
      values
        .map((value) => Number(value || 0))
        .filter((value) => Number.isFinite(value) && value > 0),
    ),
  );
}

function mergeRowsByKey<T>(rows: T[], getKey: (row: T, index: number) => string) {
  const byKey = new Map<string, T>();
  rows.forEach((row, index) => {
    byKey.set(getKey(row, index), row);
  });
  return Array.from(byKey.values());
}

function mergeLineRows(
  contracts: SynthesesGroupedContractNode[],
): NonNullable<SynthesesGroupedContractNode["lineRows"]> {
  return mergeRowsByKey(
    contracts.flatMap((contract) => Array.isArray(contract.lineRows) ? contract.lineRows : []),
    (row, index) => {
      const lineId = Number(row.lineId || 0);
      if (lineId > 0) return `line-${lineId}`;
      const realFundingId = Number(row.realFundingId || 0);
      if (realFundingId > 0) return `rf-${realFundingId}`;
      return [
        "row",
        row.year,
        row.projectId ?? "",
        toText(row.projectName),
        toText(row.title),
        row.amountEuro,
        index,
      ].join("-");
    },
  ).sort((a, b) => {
    const yearSort = Number(a.year || 0) - Number(b.year || 0);
    if (yearSort !== 0) return yearSort;
    return toText(a.projectName).localeCompare(toText(b.projectName), "fr");
  });
}

function buildProjectTotalsFromLineRows(
  lineRows: NonNullable<SynthesesGroupedContractNode["lineRows"]>,
  fallbackContracts: SynthesesGroupedContractNode[],
): NonNullable<SynthesesGroupedContractNode["projectTotals"]> {
  const rows = lineRows.length > 0
    ? lineRows.map((line) => ({
        projectId: Number(line.projectId || 0) > 0 ? Number(line.projectId) : null,
        projectName: line.projectName || null,
        amountEuro: Number(line.amountEuro || 0),
        contractCount: 1,
      }))
    : fallbackContracts.flatMap((contract) =>
        Array.isArray(contract.projectTotals) ? contract.projectTotals : [],
      );

  const byProject = new Map<string, {
    projectId: number | null;
    projectName: string | null;
    amountEuro: number;
    contractCount: number;
  }>();

  rows.forEach((row) => {
    const projectId = Number(row.projectId || 0);
    const key = projectId > 0
      ? `id-${projectId}`
      : `name-${toText(row.projectName).toLocaleLowerCase()}`;
    if (!byProject.has(key)) {
      byProject.set(key, {
        projectId: projectId > 0 ? projectId : null,
        projectName: row.projectName || null,
        amountEuro: 0,
        contractCount: 0,
      });
    }
    const current = byProject.get(key)!;
    current.amountEuro += Number(row.amountEuro || 0);
    current.contractCount += Math.max(1, Number(row.contractCount || 1));
  });

  return Array.from(byProject.values())
    .map((row) => ({
      ...row,
      amountEuro: toAmount(row.amountEuro),
      contractCount: Math.max(0, Number(row.contractCount || 0)),
    }))
    .sort((a, b) => {
      const amountSort = Number(b.amountEuro || 0) - Number(a.amountEuro || 0);
      if (amountSort !== 0) return amountSort;
      return toText(a.projectName).localeCompare(toText(b.projectName), "fr");
    });
}

function withTargetContractId<T extends {
  synthesesContractId?: number | null;
}>(row: T, targetContractId: number): T {
  return {
    ...row,
    synthesesContractId: targetContractId,
  };
}

function mergeContractRows<T extends { id?: number | null }>(
  rows: T[],
  targetContractId: number,
  getFallbackKey: (row: T, index: number) => string,
) {
  return mergeRowsByKey(
    rows.map((row) => withTargetContractId(row, targetContractId)),
    (row, index) => {
      const id = Number(row.id || 0);
      return id > 0 ? `id-${id}` : getFallbackKey(row, index);
    },
  );
}

function buildOptimisticMergedContract(
  targetContract: SynthesesGroupedContractNode,
  sourceContracts: SynthesesGroupedContractNode[],
): SynthesesGroupedContractNode {
  const targetContractId = getSynthesesContractId(targetContract);
  if (!targetContractId) return targetContract;

  const contracts = [targetContract, ...sourceContracts];
  const lineRows = mergeLineRows(contracts);
  const projectTotals = buildProjectTotalsFromLineRows(lineRows, contracts);
  const amountEuro = toAmount(
    lineRows.length > 0
      ? lineRows.reduce((sum, row) => sum + Number(row.amountEuro || 0), 0)
      : contracts.reduce((sum, contract) => sum + Number(contract.amountEuro || 0), 0),
  );
  const years = uniqueNumbers([
    ...(Array.isArray(targetContract.years) ? targetContract.years : []),
    ...sourceContracts.flatMap((contract) => Array.isArray(contract.years) ? contract.years : []),
    ...lineRows.map((row) => row.year),
  ]);
  const realFundingIds = uniqueNumbers([
    targetContract.realFundingId,
    ...(Array.isArray(targetContract.realFundingIds) ? targetContract.realFundingIds : []),
    ...sourceContracts.flatMap((contract) => [
      contract.realFundingId,
      ...(Array.isArray(contract.realFundingIds) ? contract.realFundingIds : []),
      ...(Array.isArray(contract.lineRows)
        ? contract.lineRows.map((line) => line.realFundingId)
        : []),
    ]),
  ]);

  return recomputeContractTotals({
    ...targetContract,
    amountEuro,
    amountLocal: amountEuro,
    realFundingId: realFundingIds[0] ?? targetContract.realFundingId ?? null,
    realFundingIds,
    years,
    lineRows,
    projectTotals,
    allocations: mergeContractRows(
      contracts.flatMap((contract) => Array.isArray(contract.allocations) ? contract.allocations : []),
      targetContractId,
      (row, index) =>
        `allocation-${row.desiredFundingSourceId ?? ""}-${row.project?.id ?? ""}-${row.title ?? ""}-${index}`,
    ),
    expectedPayments: mergeContractRows(
      contracts.flatMap((contract) =>
        Array.isArray(contract.expectedPayments) ? contract.expectedPayments : [],
      ),
      targetContractId,
      (row, index) =>
        `expected-${row.realFundingId ?? ""}-${row.synthesesContractLineId ?? row.lineId ?? ""}-${row.at ?? ""}-${row.amountEuro ?? ""}-${index}`,
    ),
    paidPayments: mergeContractRows(
      contracts.flatMap((contract) => Array.isArray(contract.paidPayments) ? contract.paidPayments : []),
      targetContractId,
      (row, index) =>
        `paid-${row.realFundingId ?? ""}-${row.synthesesContractLineId ?? row.lineId ?? ""}-${row.at ?? ""}-${row.amountEuro ?? ""}-${index}`,
    ),
    landPayments: mergeContractRows(
      contracts.flatMap((contract) => Array.isArray(contract.landPayments) ? contract.landPayments : []),
      targetContractId,
      (row, index) => {
        const landPaymentId = Number(row.landPaymentId || 0);
        if (landPaymentId > 0) {
          return `land-${landPaymentId}-${row.status ?? ""}-${row.projectIndicatorId ?? ""}`;
        }
        return `land-${row.projectIndicatorId ?? ""}-${row.realFundingId ?? ""}-${row.at ?? ""}-${row.status ?? ""}-${index}`;
      },
    ).sort((a, b) => toText(a.at).localeCompare(toText(b.at))),
  });
}

export function upsertSynthesesContractInGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  financeurIdentity: FinanceurIdentity,
  contract: SynthesesGroupedContractNode,
  matcher: ContractMatcher,
) {
  const data = current || createEmptyResponse();
  let foundFinanceur = false;
  let foundContract = false;

  const items = data.items.map((financeur) => {
    if (!sameFinanceur(financeur, financeurIdentity)) return financeur;
    foundFinanceur = true;

    const contracts = (Array.isArray(financeur.contracts)
      ? financeur.contracts
      : []
    ).map((item) => {
      if (!contractMatches(item, matcher)) return item;
      foundContract = true;
      return recomputeContractTotals({
        ...item,
        ...contract,
      });
    });

    if (!foundContract) {
      contracts.unshift(recomputeContractTotals(contract));
    }

    return {
      ...financeur,
      name: financeur.name || financeurIdentity.name,
      email: financeur.email || financeurIdentity.email || null,
      isVerdeo: financeur.isVerdeo || financeurIdentity.isVerdeo === true,
      contracts,
    };
  });

  if (!foundFinanceur) {
    items.unshift({
      ...createFinanceur(financeurIdentity),
      contracts: [recomputeContractTotals(contract)],
    });
  }

  return withFreshMetadata(data, items);
}

export function mergeSynthesesContractsInGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  targetContractId: number,
  sourceContractIds: number[],
) {
  if (!current) return current;

  const normalizedTargetContractId = Number(targetContractId || 0);
  const normalizedSourceIds = new Set(
    (Array.isArray(sourceContractIds) ? sourceContractIds : [])
      .map((id) => Number(id || 0))
      .filter((id) => Number.isFinite(id) && id > 0 && id !== normalizedTargetContractId),
  );

  if (
    !Number.isFinite(normalizedTargetContractId) ||
    normalizedTargetContractId <= 0 ||
    normalizedSourceIds.size === 0
  ) {
    return current;
  }

  let didMerge = false;

  const items = current.items.map((financeur) => {
    const contracts = Array.isArray(financeur.contracts) ? financeur.contracts : [];
    const targetContract = contracts.find(
      (contract) => getSynthesesContractId(contract) === normalizedTargetContractId,
    );
    if (!targetContract) return financeur;

    const sourceContracts = contracts.filter((contract) => {
      const contractId = getSynthesesContractId(contract);
      return !!contractId && normalizedSourceIds.has(contractId);
    });
    if (sourceContracts.length === 0) return financeur;

    didMerge = true;
    const mergedContract = buildOptimisticMergedContract(targetContract, sourceContracts);
    const nextContracts = contracts
      .map((contract) => {
        const contractId = getSynthesesContractId(contract);
        if (contractId === normalizedTargetContractId) return mergedContract;
        if (contractId && normalizedSourceIds.has(contractId)) return null;
        return contract;
      })
      .filter((contract): contract is SynthesesGroupedContractNode => !!contract);

    return {
      ...financeur,
      contracts: nextContracts,
    };
  });

  return didMerge ? withFreshMetadata(current, items) : current;
}

export function removeSynthesesContractsFromGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  matcher: ContractMatcher,
) {
  if (!current) return current;

  const items = current.items
    .map((financeur) => ({
      ...financeur,
      contracts: (Array.isArray(financeur.contracts)
        ? financeur.contracts
        : []
      ).filter((contract) => !contractMatches(contract, matcher)),
    }))
    .filter((financeur) => {
      if (financeur.contracts.length > 0) return true;
      return Number(financeur.id || 0) > 0;
    });

  return withFreshMetadata(current, items);
}

export function updateSynthesesContractInGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  matcher: ContractMatcher,
  updater: (
    contract: SynthesesGroupedContractNode,
  ) => SynthesesGroupedContractNode,
) {
  if (!current) return current;

  const items = current.items.map((financeur) => ({
    ...financeur,
    contracts: (Array.isArray(financeur.contracts) ? financeur.contracts : [])
      .map((contract) =>
        contractMatches(contract, matcher)
          ? recomputeContractTotals(updater(contract))
          : contract,
      ),
  }));

  return withFreshMetadata(current, items);
}

export function replaceLandPaymentRowsInGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  matcher: ContractMatcher,
  landPaymentId: number | null,
  rows: SynthesesGroupedContractNode["landPayments"],
) {
  return updateSynthesesContractInGroupedData(current, matcher, (contract) => {
    const normalizedLandPaymentId = Number(landPaymentId || 0);
    const baseRows = Array.isArray(contract.landPayments)
      ? contract.landPayments
      : [];
    const withoutCurrent = Number.isFinite(normalizedLandPaymentId) && normalizedLandPaymentId !== 0
      ? baseRows.filter(
          (payment) =>
            Number(payment.landPaymentId || payment.id || 0) !==
            normalizedLandPaymentId,
        )
      : baseRows;

    return {
      ...contract,
      landPayments: [...withoutCurrent, ...rows].sort((a, b) =>
        toText(a.at).localeCompare(toText(b.at)),
      ),
    };
  });
}

export function replaceReconciledLandPaymentRowsInGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  matcher: ContractMatcher,
  landPaymentId: number | null,
  projectIndicatorIds: number[],
  rows: SynthesesGroupedContractNode["landPayments"],
  sourceLandPaymentIds: number[] = [],
) {
  return updateSynthesesContractInGroupedData(current, matcher, (contract) => {
    const normalizedLandPaymentId = Number(landPaymentId || 0);
    const sourceLandPaymentIdSet = new Set(
      (Array.isArray(sourceLandPaymentIds) ? sourceLandPaymentIds : [])
        .map((id) => Number(id || 0))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    const selectedIndicatorIds = new Set(
      (Array.isArray(projectIndicatorIds) ? projectIndicatorIds : [])
        .map((id) => Number(id || 0))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    const baseRows = Array.isArray(contract.landPayments)
      ? contract.landPayments
      : [];
    const withoutLinkedRows = baseRows.filter((payment) => {
      const paymentLandPaymentId = Number(payment.landPaymentId || payment.id || 0);
      if (
        Number.isFinite(normalizedLandPaymentId) &&
        normalizedLandPaymentId !== 0 &&
        paymentLandPaymentId === normalizedLandPaymentId
      ) {
          return false;
      }
      if (sourceLandPaymentIdSet.has(paymentLandPaymentId)) {
        return false;
      }

      const projectIndicatorId = Number(payment.projectIndicatorId || 0);
      return !(projectIndicatorId > 0 && selectedIndicatorIds.has(projectIndicatorId));
    });

    return {
      ...contract,
      landPayments: [...withoutLinkedRows, ...rows].sort((a, b) =>
        toText(a.at).localeCompare(toText(b.at)),
      ),
    };
  });
}

export function replaceLandPaymentStatusRowsInGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  matcher: ContractMatcher,
  landPaymentId: number | null,
  status: number,
  rows: SynthesesGroupedContractNode["landPayments"],
) {
  return updateSynthesesContractInGroupedData(current, matcher, (contract) => {
    const normalizedLandPaymentId = Number(landPaymentId || 0);
    const normalizedStatus = Number(status || 0);
    const baseRows = Array.isArray(contract.landPayments)
      ? contract.landPayments
      : [];
    const withoutCurrent =
      Number.isFinite(normalizedLandPaymentId) && normalizedLandPaymentId !== 0
        ? baseRows.filter(
            (payment) =>
              Number(payment.landPaymentId || payment.id || 0) !==
                normalizedLandPaymentId ||
              Number(payment.status || 0) !== normalizedStatus,
          )
        : baseRows;

    return {
      ...contract,
      landPayments: [...withoutCurrent, ...rows].sort((a, b) =>
        toText(a.at).localeCompare(toText(b.at)),
      ),
    };
  });
}

export function updateLandPaymentRowsInGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  matcher: ContractMatcher,
  landPaymentId: number | null,
  updater: (
    payment: SynthesesGroupedContractNode["landPayments"][number],
  ) => SynthesesGroupedContractNode["landPayments"][number],
) {
  return updateSynthesesContractInGroupedData(current, matcher, (contract) => {
    const normalizedLandPaymentId = Number(landPaymentId || 0);
    if (!Number.isFinite(normalizedLandPaymentId) || normalizedLandPaymentId === 0) {
      return contract;
    }

    return {
      ...contract,
      landPayments: (Array.isArray(contract.landPayments)
        ? contract.landPayments
        : []
      ).map((payment) =>
        Number(payment.landPaymentId || payment.id || 0) === normalizedLandPaymentId
          ? updater(payment)
          : payment,
      ),
    };
  });
}

export function removeLandPaymentRowsFromGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  landPaymentId: number,
) {
  if (!current) return current;

  const items = current.items.map((financeur) => ({
    ...financeur,
    contracts: (Array.isArray(financeur.contracts) ? financeur.contracts : [])
      .map((contract) =>
        recomputeContractTotals({
          ...contract,
          landPayments: (Array.isArray(contract.landPayments)
            ? contract.landPayments
            : []
          ).filter(
            (payment) =>
              Number(payment.landPaymentId || payment.id || 0) !== landPaymentId,
          ),
        }),
      ),
  }));

  return withFreshMetadata(current, items);
}

export function moveLandPaymentRowsInGroupedData(
  current: SynthesesGroupedContractsResponse | null,
  landPaymentId: number,
  targetLineId: number,
) {
  if (!current) return current;

  const normalizedLandPaymentId = Number(landPaymentId || 0);
  const normalizedTargetLineId = Number(targetLineId || 0);
  if (
    !Number.isFinite(normalizedLandPaymentId) ||
    normalizedLandPaymentId <= 0 ||
    !Number.isFinite(normalizedTargetLineId) ||
    normalizedTargetLineId <= 0
  ) {
    return current;
  }

  const movingRows: SynthesesGroupedContractNode["landPayments"] = [];
  let targetContractNodeId = "";
  let targetContractSynthesesId: number | null = null;
  let targetLine:
    | NonNullable<SynthesesGroupedContractNode["lineRows"]>[number]
    | null = null;

  current.items.forEach((financeur) => {
    (Array.isArray(financeur.contracts) ? financeur.contracts : []).forEach((contract) => {
      (Array.isArray(contract.landPayments) ? contract.landPayments : []).forEach((payment) => {
        if (Number(payment.landPaymentId || payment.id || 0) === normalizedLandPaymentId) {
          movingRows.push(payment);
        }
      });

      const matchingLine = (Array.isArray(contract.lineRows) ? contract.lineRows : []).find(
        (line) => Number(line.lineId || 0) === normalizedTargetLineId,
      );
      if (matchingLine && !targetLine) {
        targetLine = matchingLine;
        targetContractNodeId = toText(contract.id);
        targetContractSynthesesId = getSynthesesContractId(contract);
      }
    });
  });

  if (movingRows.length === 0 || !targetLine || !targetContractNodeId) {
    return current;
  }

  const targetRealFundingId = Number(targetLine.realFundingId || 0);
  const targetProjectId = Number(targetLine.projectId || 0);
  const targetProjectName = toText(targetLine.projectName);
  const targetLineTitle = toText(targetLine.title);
  const movedRows = movingRows.map((payment) => ({
    ...payment,
    source: "land_payment",
    realFundingId:
      Number.isFinite(targetRealFundingId) && targetRealFundingId > 0
        ? targetRealFundingId
        : payment.realFundingId ?? null,
    synthesesContractId: targetContractSynthesesId,
    synthesesContractLineId: normalizedTargetLineId,
    lineId: normalizedTargetLineId,
    lineTitle: targetLineTitle || payment.lineTitle || null,
    projectId:
      Number.isFinite(targetProjectId) && targetProjectId > 0
        ? targetProjectId
        : payment.projectId ?? null,
    projectName: targetProjectName || payment.projectName || null,
  }));

  const items = current.items.map((financeur) => ({
    ...financeur,
    contracts: (Array.isArray(financeur.contracts) ? financeur.contracts : []).map(
      (contract) => {
        const remainingLandPayments = (Array.isArray(contract.landPayments)
          ? contract.landPayments
          : []
        ).filter(
          (payment) =>
            Number(payment.landPaymentId || payment.id || 0) !==
            normalizedLandPaymentId,
        );
        const isTargetContract = toText(contract.id) === targetContractNodeId;

        return recomputeContractTotals({
          ...contract,
          landPayments: (
            isTargetContract
              ? [...remainingLandPayments, ...movedRows]
              : remainingLandPayments
          ).sort((a, b) => toText(a.at).localeCompare(toText(b.at))),
        });
      },
    ),
  }));

  return withFreshMetadata(current, items);
}
